/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { EuiTextProps } from '@elastic/eui'
import { EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import prettySize from '../../lib/prettySize'
import Percentage from '../../lib/Percentage'

import type { FunctionComponent, ReactNode } from 'react'

type Props = Pick<EuiTextProps, 'color'> & {
  pressure: number
  pressureFormatter: ((pressure: number) => ReactNode) | undefined
  total: number
  hideTotal: boolean | undefined
  statFormatter: ((stat: number) => ReactNode) | undefined
}

const StatePressureLabel: FunctionComponent<Props> = ({
  pressure,
  pressureFormatter = (p) => <Percentage value={p / 100} />,
  total,
  hideTotal,
  color,
  statFormatter = prettySize,
}) => {
  if (pressure == null || Number.isNaN(pressure) || total == null) {
    return (
      <EuiText size='s' color={color}>
        <FormattedMessage id='stat-pressure-label.no-data' defaultMessage='No data' />
      </EuiText>
    )
  }

  const pressurePercent = pressureFormatter(pressure)

  if (hideTotal) {
    return (
      <EuiText size='s' color={color}>
        {pressurePercent}
      </EuiText>
    )
  }

  return (
    <EuiText size='s' color={color}>
      <FormattedMessage
        id='stat-pressure-label.pressure-percentage'
        defaultMessage='{ pressurePercent } of { total }'
        values={{
          pressurePercent,
          total: statFormatter(total),
        }}
      />
    </EuiText>
  )
}

export default StatePressureLabel
